// Workspace-wide Notes view. Lists every note across companies, contacts,
// engagements, and buyer_engagements. Filter chips for type / pinned, plus a
// server-side search box; clicking the "linked to" pill jumps to the entity.

const NOTE_TYPE_HUE = {
  general:    'slate',
  call:       'amber',
  meeting:    'violet',
  email:      'teal',
  memo:       'slate',
  ai_summary: 'blue',
  internal:   'slate',
};

const NOTE_TYPES = [
  { key: 'all',        label: 'All' },
  { key: 'pinned',     label: 'Pinned' },
  { key: 'call',       label: 'Calls' },
  { key: 'meeting',    label: 'Meetings' },
  { key: 'email',      label: 'Emails' },
  { key: 'memo',       label: 'Memos' },
  { key: 'ai_summary', label: 'AI summaries' },
  { key: 'internal',   label: 'Internal' },
  { key: 'general',    label: 'General' },
];

// ────────────────────────────────────────────────────────────────────────
// Table mode. Notes ship as a card feed — the body preview is the point, and
// a card holds far more text than a cell. But a feed is the wrong shape for
// "show me every call this quarter with its company and author side by side",
// so the view offers both and remembers which one each person picked (105).
//
// Headers are deliberately NOT sortable: /api/notes has no sort parameter, so
// the only thing a header click could do is reorder the pages already loaded
// out of the 183K available — an affordance that looks like sorting and isn't.
// The server order (newest first) is the one true order here.
//
// Notes rows are raw API objects, not adapted — hence n.company_name etc.
const NOTE_COLUMNS = [
  {
    key: 'date', label: 'Date', extra: 20, fixed: true,
    accessor: (n) => window.fmtDate(n.dc_created_at || n.created_at),
    xget: (n) => n.dc_created_at || n.created_at || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-2)', borderRight: '1px solid var(--border)' }}>
        {n.is_pinned && <span title="Pinned" style={{ color: 'var(--amber-fg)', fontSize: 9 }}>●</span>}
        {window.fmtDate(n.dc_created_at || n.created_at) || '—'}
      </div>
    ),
  },
  {
    key: 'type', label: 'Type', extra: 18,
    accessor: (n) => (window.NoteTypeLabel ? window.NoteTypeLabel(n) : n.note_type),
    xget: (n) => n.note_type || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', borderRight: '1px solid var(--border)' }}>
        <Pill hue={NOTE_TYPE_HUE[n.note_type] || 'slate'} sz="sm">
          {window.NoteTypeLabel ? window.NoteTypeLabel(n) : n.note_type}
        </Pill>
      </div>
    ),
  },
  {
    key: 'title', label: 'Title', accessor: (n) => n.title || (n.body || '').slice(0, 40),
    xget: (n) => n.title || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', gap: 6, borderRight: '1px solid var(--border)', overflow: 'hidden' }}>
        <span style={{
          fontSize: 12.5, fontWeight: n.title ? 600 : 400,
          color: n.title ? 'var(--text)' : 'var(--text-3)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {n.title || (n.body || '').replace(/\s+/g, ' ').slice(0, 80) || '—'}
        </span>
        {(n.attachments || []).length > 0 && (
          <span title={`${n.attachments.length} attachment(s)`} style={{ fontSize: 10, color: 'var(--text-4)', flexShrink: 0 }}>
            📎{n.attachments.length}
          </span>
        )}
      </div>
    ),
  },
  {
    key: 'author', label: 'Author', accessor: (n) => n.author_name,
    xget: (n) => n.author_name || '',
    render: (n, ctx, col) => (
      <div key={col.key} onClick={(e) => e.stopPropagation()} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)', overflow: 'hidden' }}>
        {n.author_name ? <UserLink id={n.author_id} name={n.author_name} /> : <span style={{ color: 'var(--text-4)' }}>—</span>}
      </div>
    ),
  },
  {
    key: 'company', label: 'Company', accessor: (n) => n.company_name,
    xget: (n) => n.company_name || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-2)', borderRight: '1px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {n.company_name || '—'}
      </div>
    ),
  },
  {
    key: 'engagement', label: 'Engagement', accessor: (n) => n.engagement_name,
    xget: (n) => n.engagement_name || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', borderRight: '1px solid var(--border)', overflow: 'hidden' }}>
        {n.engagement_id ? (
          <button onClick={(e) => { e.stopPropagation(); ctx.onOpenDeal(n.engagement_id); }}
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, color: 'var(--accent-text)', textAlign: 'left',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%',
            }}>
            {n.engagement_name || 'Open deal'}
          </button>
        ) : <span style={{ fontSize: 12, color: 'var(--text-4)' }}>—</span>}
      </div>
    ),
  },
  {
    key: 'contact', label: 'Contact', accessor: (n) => n.contact_name,
    xget: (n) => n.contact_name || '',
    render: (n, ctx, col) => (
      <div key={col.key} style={{ width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text-2)', borderRight: col.last ? 'none' : '1px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {n.contact_name || '—'}
      </div>
    ),
  },

  // ── Off by default.
  ...[
    // The full body is exported unconditionally (see NOTE_EXPORT_ALWAYS), so
    // this column would only duplicate it in truncated form.
    { key: 'preview', label: 'Preview', width: 320, xskip: true,
      text: (n) => (n.body || '').replace(/\s+/g, ' ').slice(0, 160),
      desc: 'First ~160 characters of the note body.' },
    { key: 'subtype', label: 'DC subtype', text: (n) => n.dc_note_subtype,
      desc: "DealCloud's note subtype — 'Intro call with seller' is the real D/S call marker." },
    { key: 'source',  label: 'Source',    text: (n) => n.source },
    { key: 'syncStatus', label: 'DC sync', text: (n) => n.dc_sync_status },
    { key: 'attachments', label: 'Attachments', align: 'right', text: (n) => String((n.attachments || []).length || '') },
    { key: 'pinned',  label: 'Pinned',    text: (n) => (n.is_pinned ? 'Yes' : '') },
  ].map((c) => ({
    key: c.key, label: c.label, desc: c.desc, align: c.align, def: false, group: 'More fields',
    xskip: c.xskip,
    accessor: (n) => c.text(n) || '',
    render: (n, ctx, col) => {
      const v = c.text(n);
      return (
        <div key={col.key} style={{
          width: col.w, padding: '0 10px', display: 'flex', alignItems: 'center',
          justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          fontSize: 11.5, color: v ? 'var(--text-2)' : 'var(--text-4)',
          borderRight: col.last ? 'none' : '1px solid var(--border)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} className={c.align === 'right' ? 'num' : ''}>
          {v || '—'}
        </div>
      );
    },
  })),
];

// openNoteId / setOpenNoteId are owned by app.jsx so the open note lives in
// the URL as #/notes/<id> — see router.jsx. That makes the address bar itself
// the shareable link, and lets a note shared from any other page open here.
// A phone gets its own feed (PhoneNotesView, below); everything else this
// view. Two components rather than a branch inside one, so a device change
// can never change a hook order.
const NotesView = (props) => {
  const device = window.useDevice ? window.useDevice() : (window.AtlasDevice || {});
  return device.phone ? <PhoneNotesView {...props} /> : <NotesDesktopView {...props} />;
};

const NotesDesktopView = ({ setView, setDealId, activeUser, openNoteId, setOpenNoteId }) => {
  // iPad: the type tabs scroll sideways instead of pushing the search box
  // off a portrait screen; rows don't keep a hover tint after a tap.
  const touch = !!(window.AtlasDevice && window.AtlasDevice.touch);
  const [tab,   setTab]   = React.useState('all');
  const [composing, setComposing] = React.useState(false);
  const [q,     setQ]     = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [items, setItems] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  // AI previews for long notes — collapsed cards show these instead of raw
  // text (hook defined in company_detail.jsx, shared via window).
  const aiPreviews = (window.useNoteAiPreviews || (() => ({})))(items);
  // Cursor pagination — same shape as deals/companies. Single PAGE_SIZE
  // keeps the server's offset math correct, `loadedAll` is the safety
  // stop on empty responses.
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const PAGE_SIZE = 120;

  // 200ms debounce on the search input — typing shouldn't fire a request per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const buildParams = React.useCallback(() => {
    const p = {};
    if (tab === 'pinned') p.pinned_only = true;
    else if (tab !== 'all') p.note_type = tab;
    if (debouncedQ) p.q = debouncedQ;
    return p;
  }, [tab, debouncedQ]);

  // Stale-response guard — a slow response for an older query/tab must not
  // overwrite fresher rows (debounced-search race).
  const reqSeq = React.useRef(0);

  const fetchPage = React.useCallback(async (p, append) => {
    const seq = ++reqSeq.current;
    setPaging(true);
    try {
      const res = await window.AtlasApi.listNotes({
        ...buildParams(), page: p, limit: PAGE_SIZE,
      });
      if (seq !== reqSeq.current) return;   // superseded — drop silently
      const rows = (res && res.items) || [];
      const newTotal = res?.total ?? rows.length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (rows.length === 0 || next.length >= newTotal) setLoadedAll(true);
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); setLoadedAll(true); }
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [buildParams]);

  const reload = React.useCallback(() => {
    setItems(null);
    setPage(1);
    setLoadedAll(false);
    return fetchPage(1, false);
  }, [fetchPage]);

  React.useEffect(() => { reload(); }, [reload]);

  // Whole-view export (notes have no row selection). Refetches the current
  // tab+search in one call when the pager hasn't finished; 183K notes means
  // the server's 10K cap can truncate — the menu says so. Full body included:
  // that's the point of exporting notes (exportCsv escapes newlines).
  const EXPORT_CAP = 10000;
  // Appended to every notes export regardless of layout. The full body is
  // the reason anyone exports notes (exportCsv escapes the newlines), and the
  // Atlas id lets a file be joined back — neither should depend on which
  // columns happen to be on screen. Everything else comes from the visible
  // columns; see window.exportColumnsFor.
  // Per-user layout + cards/table mode (105). Cards stays the default: the
  // body preview is what most people come to Notes for.
  const columns = window.useColumnPrefs('notes', NOTE_COLUMNS, {
    rows: items || [], fitMax: 320, defaultMode: 'cards',
  });
  const noteCellCtx = React.useMemo(() => ({
    onOpenDeal: (id) => { setView('deal'); setDealId(id); },
  }), [setView, setDealId]);

  const NOTE_EXPORT_ALWAYS = [
    { key: 'body', label: 'Note',     get: (n) => n.body || '' },
    { key: 'id',   label: 'Atlas id', get: (n) => n.id },
  ];
  const doExport = React.useCallback(async (format) => {
    let data;
    if (loadedAll) {
      data = items || [];
    } else {
      // /api/notes caps limit at 500, so the whole-view export pages up to
      // EXPORT_CAP instead of asking for it in one shot — a single
      // limit=10000 call 422s, which silently failed every whole-view export.
      const PAGE = 500;
      const params = buildParams();
      data = [];
      for (let page = 1; data.length < EXPORT_CAP; page++) {
        const res = await window.AtlasApi.listNotes({ ...params, page, limit: PAGE });
        const batch = (res && res.items) || [];
        data.push(...batch);
        if (batch.length < PAGE) break;          // server ran out of rows
      }
      data = data.slice(0, EXPORT_CAP);
    }
    // The file mirrors the grid: the columns you can see, in your order.
    const xcols = window.exportColumnsFor(columns.cols, NOTE_EXPORT_ALWAYS);
    const records = window.exportRecords(xcols, data);
    const name = `notes-${tab}-${window.localISODate()}`;
    if (format === 'csv') window.exportCsv(`${name}.csv`, xcols, records);
    else window.exportJson(`${name}.json`, records);
  }, [loadedAll, items, buildParams, tab, columns.cols]);

  // IntersectionObserver — loads next page when the sentinel scrolls into
  // view (within 240px). Both gates must be true: server still has rows
  // AND the last fetch wasn't empty.
  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) {
        fetchPage(page + 1, true);
      }
    }, { rootMargin: '240px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Notes']}
        title="Notes"
        meta={items === null ? 'Loading…' : `${items.length} of ${total.toLocaleString()} notes`}
        actions={(
          <>
            <window.ExportMenu
              disabled={items === null || !(items || []).length}
              viewCount={loadedAll ? (items || []).length : total}
              capNote={!loadedAll && total > EXPORT_CAP
                ? `Caps at the first ${EXPORT_CAP.toLocaleString()} notes — narrow the tab/search for a complete file.`
                : null}
              onExport={doExport}
            />
            {/* Cards vs table. The choice rides the same per-user prefs blob
                as the column layout, so it survives a reload and a new
                machine. */}
            <div style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden', height: 26 }}>
              {[['cards', 'Cards'], ['table', 'Table']].map(([m, label]) => (
                <button key={m} onClick={() => columns.setMode(m)} style={{
                  padding: '0 10px', border: 'none', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: columns.mode === m ? 600 : 500, fontFamily: 'inherit',
                  background: columns.mode === m ? 'var(--surface-hover)' : 'transparent',
                  color: columns.mode === m ? 'var(--text)' : 'var(--text-3)',
                }}>{label}</button>
              ))}
            </div>
            {columns.mode === 'table' && (
              <window.ColumnsMenu
                catalog={NOTE_COLUMNS}
                cols={columns.cols}
                toggle={columns.toggle}
                move={columns.move}
                reset={columns.reset}
              />
            )}
            <Btn size="sm" variant="outline" icon={<Icon.Plus size={11} />}
              onClick={() => setComposing(true)} disabled={!activeUser}>
              New note
            </Btn>
          </>
        )}
      />

      {/* Filter chips + search */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 1, padding: '0 16px',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36,
      }}>
        {(() => {
          const tabs = NOTE_TYPES.map(({ key, label }) => {
            const active = tab === key;
            return (
              <button key={key} onClick={() => setTab(key)} style={{
                padding: '0 10px', height: 36, border: 'none', background: 'transparent',
                fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
                color: active ? 'var(--text)' : 'var(--text-3)',
                borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
                marginBottom: -1,
                ...(touch ? { flexShrink: 0, whiteSpace: 'nowrap' } : null),
              }}>
                {label}
              </button>
            );
          });
          return touch ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, height: 36,
                          overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
              {tabs}
            </div>
          ) : tabs;
        })()}
        <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                      ...(touch ? { flexShrink: 0, paddingLeft: 8 } : null) }}>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search notes…"
            style={{
              width: touch ? 200 : 240, padding: '4px 10px', fontSize: 12,
              border: '1px solid var(--border)', borderRadius: 6, height: touch ? 32 : 26, fontFamily: 'inherit',
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {items === null && <SkeletonRows rows={10} variant="generic" />}

        {items && items.length === 0 && (
          <div style={{ padding: 32, maxWidth: 640 }}>
            <div style={{
              background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
              padding: '32px 24px',
            }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                {debouncedQ || tab !== 'all' ? 'No notes match this filter' : 'No notes yet'}
              </h3>
            </div>
          </div>
        )}

        {items && items.length > 0 && columns.mode === 'table' && (
          <div style={{ minWidth: 'max-content', background: 'var(--surface)' }}>
            <div style={{
              display: 'flex', height: 30, borderBottom: '1px solid var(--border)',
              background: 'var(--surface-2)', position: 'sticky', top: 0, zIndex: 1,
              fontSize: 11, color: 'var(--text-3)', fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {columns.cols.map((c) => (
                <window.SortableHeader key={c.key} col={c} sortable={false}
                  onResizeStart={touch ? undefined : columns.startResize(c.key)} />
              ))}
            </div>
            {items.map((n) => (
              <div key={n.id} onClick={() => setOpenNoteId(n.id)}
                style={{
                  display: 'flex', minHeight: 34, alignItems: 'center',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                }}
                onMouseEnter={window.hoverOnly((e) => { e.currentTarget.style.background = 'var(--surface-2)'; })}
                onMouseLeave={(e) => e.currentTarget.style.background = ''}
              >
                {columns.cols.map((col) => col.render(n, noteCellCtx, col))}
              </div>
            ))}
          </div>
        )}

        {items && items.length > 0 && columns.mode !== 'table' && (
          <div style={{ background: 'var(--surface)' }}>
            {items.map((n, i) => {
              const hue = NOTE_TYPE_HUE[n.note_type] || 'slate';
              const when = window.NoteCardDate(n);
              const linkLabel =
                n.engagement_id     ? `Engagement: ${n.engagement_name || '—'}` :
                n.company_id        ? `Company: ${n.company_name || '—'}`       :
                n.contact_id        ? `Contact: ${n.contact_name || '—'}`       :
                n.buyer_engagement_id ? 'Buyer engagement' : null;
              const linkAction =
                n.engagement_id ? () => { setView('deal'); setDealId(n.engagement_id); } :
                n.company_id    ? () => window.AtlasNav?.goToCompany?.(n.company_id) :
                n.contact_id    ? () => window.AtlasNav?.goToContact?.(n.contact_id) :
                null;

              return (
                <div key={n.id}
                  // Clicking anywhere on the row opens the modal. Inline
                  // interactive elements (expand/collapse, linked-entity
                  // button) stopPropagation so they don't open it too.
                  onClick={() => setOpenNoteId(n.id)}
                  style={{
                    display: 'flex', gap: 12, padding: '12px 16px',
                    borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={window.hoverOnly((e) => { e.currentTarget.style.background = 'var(--surface-2)'; })}
                  onMouseLeave={(e) => e.currentTarget.style.background = ''}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 26, height: 26, borderRadius: 999, flexShrink: 0, marginTop: 1,
                    background: HUE[hue].bg, color: HUE[hue].fg,
                  }}>
                    <Icon.Doc size={12} />
                  </span>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)', flexWrap: 'wrap' }}>
                      {n.author_name && <UserLink id={n.author_id} name={n.author_name} style={{ color: 'var(--text-2)', fontWeight: 500 }} />}
                      {n.author_name && <span>·</span>}
                      <span>{when.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {/* Created / edited. The date above is the note's own
                          date — for a call note that's the backdated call
                          date, so show when it was actually logged when the
                          two differ. "edited" is only shown for notes Atlas
                          owns: DealCloud exposes no modified timestamp for
                          notes, and on mirrored rows updated_at is rewritten
                          by every sync pass. */}
                      {(() => {
                        const created = n.dc_created_at || n.created_at;
                        if (!created) return null;
                        const createdD = new Date(created);
                        const logged = createdD.toDateString() !== when.toDateString()
                          ? createdD : null;
                        const atlasOwned = n.source !== 'dealcloud_import';
                        const edited = atlasOwned && n.updated_at
                          && (new Date(n.updated_at) - createdD) > 60_000
                          ? new Date(n.updated_at) : null;
                        return (
                          <>
                            {logged && (
                              <span title={`Logged ${logged.toLocaleString()}`} style={{ color: 'var(--text-4)' }}>
                                logged {window.fmtDate(created)}
                              </span>
                            )}
                            {edited && (
                              <span title={`Edited in Atlas ${edited.toLocaleString()}`} style={{ color: 'var(--text-4)' }}>
                                edited {window.fmtDate(n.updated_at)}
                              </span>
                            )}
                          </>
                        );
                      })()}
                      <Pill hue={hue} sz="sm">{window.NoteTypeLabel ? window.NoteTypeLabel(n) : n.note_type}</Pill>
                      {n.source === 'dealcloud_import' && <Pill hue="slate" sz="sm">imported</Pill>}
                      {n.dc_sync_status === 'synced' && <span title="Pushed to DealCloud"><Pill hue="green" sz="sm">DC ✓</Pill></span>}
                      {n.dc_sync_status === 'pending' && <span title="Queued for DealCloud"><Pill hue="amber" sz="sm">DC …</Pill></span>}
                      {n.dc_sync_status === 'failed' && <span title="DealCloud push failed — see Settings → DealCloud sync"><Pill hue="rose" sz="sm">DC ✗</Pill></span>}
                      {n.is_pinned && <Pill hue="amber" sz="sm">pinned</Pill>}
                      {n.attachments && n.attachments.length > 0 && (
                        <span title={`${n.attachments.length} attachment${n.attachments.length === 1 ? '' : 's'} — open the note to manage`}>
                          <Pill hue="slate" sz="sm">📎 {n.attachments.length}</Pill>
                        </span>
                      )}
                      {linkLabel && (
                        <button
                          onClick={(e) => { e.stopPropagation(); linkAction && linkAction(); }}
                          disabled={!linkAction}
                          style={{
                            background: 'transparent', border: 'none', cursor: linkAction ? 'pointer' : 'default',
                            color: 'var(--accent-text)', fontSize: 11.5, padding: 0,
                            textDecoration: 'underline', textUnderlineOffset: 2,
                          }}>{linkLabel}</button>
                      )}
                      {/* Cross-filed tags (DealCloud multi-ref Companies/Contacts) */}
                      {(n.tagged_companies || []).map((c) => (
                        <span key={`tco-${c.id}`} title="Tagged company"><Pill hue="teal" sz="sm">🏢 {c.name || 'Company'}</Pill></span>
                      ))}
                      {(n.tagged_contacts || []).map((c) => (
                        <span key={`tct-${c.id}`} title="Tagged contact"><Pill hue="slate" sz="sm">👤 {c.name || 'Contact'}</Pill></span>
                      ))}
                    </div>

                    {n.title && (
                      <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, marginTop: 3,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.title}</div>
                    )}
                    {window.NotePreviewBody && <window.NotePreviewBody n={n} aiPreviews={aiPreviews} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* IntersectionObserver target — rendered once below the list while
            more pages are available. 240px rootMargin in the effect means
            this fires before the user actually hits the bottom. */}
        {hasMore && items !== null && (
          <div ref={sentinelRef} style={{ height: 1 }} />
        )}
        {paging && items !== null && items.length > 0 && (
          <SkeletonRows rows={3} variant="generic" />
        )}
        {!hasMore && items !== null && items.length > 0 && (
          <div style={{
            padding: '14px 16px', textAlign: 'center', fontSize: 11.5,
            color: 'var(--text-4)', background: 'var(--surface)',
          }}>
            End of list · {items.length.toLocaleString()} {items.length === 1 ? 'note' : 'notes'}
          </div>
        )}
      </div>
      <NoteModal noteId={openNoteId} onClose={() => setOpenNoteId(null)} onChange={reload} />
      {composing && (
        <NewNoteDrawer
          activeUser={activeUser}
          onClose={() => setComposing(false)}
          onCreated={() => { setComposing(false); reload(); }}
        />
      )}
    </div>
  );
};

// "New note" from the workspace view — unlike the record-page composers the
// note has no implicit home, so the drawer asks which company it belongs to.
const NewNoteDrawer = ({ activeUser, onClose, onCreated }) => {
  // Phone: the drawer is already a full-screen sheet; its side-by-side rows
  // (company + New company, Type + Subtype) stack into one column there.
  const device = window.useDevice ? window.useDevice() : (window.AtlasDevice || {});
  const P = !!device.phone;
  const [companyId, setCompanyId] = React.useState('');
  const [companies, setCompanies] = React.useState([]);
  const [draft, setDraft] = React.useState({ body: '', title: '', note_type: 'general', dc_note_subtype: '', call_date: '', tag_companies: [], tag_contacts: [], lead: null });
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  // Pasted screenshots land in the same pending list the picker feeds.
  const addPendingFiles = (list) => setPendingFiles((prev) => [...prev, ...Array.from(list || [])]);
  const [creatingCompany, setCreatingCompany] = React.useState(false);
  // Lazy lookups for the stacked NewCompanyDrawer — only if "+ New company" opens.
  const [companyLookups, setCompanyLookups] = React.useState(null); // {industries, members}

  const openCompanyCreate = () => {
    if (companyLookups === null) {
      Promise.all([
        window.AtlasApi.listIndustries().catch(() => []),
        window.AtlasData.loadTeamMembers().catch(() => []),
      ]).then(([industries, members]) => setCompanyLookups({
        industries: Array.isArray(industries) ? industries : [],
        members: Array.isArray(members) ? members : [],
      }));
    }
    setCreatingCompany(true);
  };

  // `companies` holds static picker extras only (a just-created company) —
  // the Combobox searches the server for everything else.

  const canSave = draft.body.trim() && companyId && !saving;
  // Call date applies when the type is Call OR the subtype logs a KPI call
  // (D/S, B/S) — the latter also stamps the company's call-milestone field.
  const showCallDate = draft.note_type === 'call' || (window.KPI_CALL_SUBTYPES || []).includes(draft.dc_note_subtype);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const created = await window.AtlasApi.createNote({
        body: draft.body.trim(),
        title: draft.title.trim() || null,
        note_type: draft.note_type,
        dc_note_subtype: draft.dc_note_subtype || null,
        call_date: (showCallDate && draft.call_date) || null,
        company_id: companyId,
        // Cross-filing: the note also shows on these records' pages and
        // rides the DC Companies/Contacts arrays.
        tag_company_ids: draft.tag_companies.map((c) => c.value),
        tag_contact_ids: draft.tag_contacts.map((c) => c.value),
        author_id: activeUser?.id || null,
        author_name: activeUser?.full_name || null,
      });
      await window.uploadPendingAttachments(created, pendingFiles);
      // The lead-source row (shown only while the picked company has none)
      // saves after the note; a failure there must not undo the note.
      const stamped = await window.saveLeadNudge(companyId, draft.lead).catch(() => false);
      pushToast({ type: 'success', title: stamped ? 'Note added · lead source recorded' : 'Note added' });
      onCreated();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title="New note"
      footer={(
        <>
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={!canSave}>
            {saving ? 'Saving…' : 'Save note'}
          </Btn>
        </>
      )}
    >
      <Field label="Company" hint="Search by name, or create it right here.">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', ...(P ? { flexWrap: 'wrap', rowGap: 8 } : null) }}>
          <div style={{ flex: P ? '1 1 100%' : 1, minWidth: 0 }}>
            <Combobox value={companyId} onChange={(e) => setCompanyId(e.target.value)}
              placeholder="Search companies…"
              options={companies}
              loadOptions={window.searchCompanyOptions} />
          </div>
          <Btn size="xs" variant="outline" icon={<Icon.Plus size={10} />}
            onClick={openCompanyCreate} title="Create a company that isn't in Atlas yet">
            New company
          </Btn>
        </div>
      </Field>
      <Field label="Title" hint="Optional.">
        <TextInput value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
      </Field>
      <div style={{ display: 'flex', gap: 8, ...(P ? { flexDirection: 'column', gap: 0 } : null) }}>
        <div style={{ flex: 1 }}>
          <Field label="Type">
            <Combobox value={draft.note_type} onChange={(e) => setDraft({
                ...draft, note_type: e.target.value,
                // Logging a call defaults its call date to today; backfills
                // just pick the real date.
                call_date: e.target.value === 'call' ? (draft.call_date || window.localISODate()) : draft.call_date,
              })}
              options={[
                { value: 'general', label: 'General' },
                { value: 'call',    label: 'Call' },
                { value: 'meeting', label: 'Meeting' },
                { value: 'email',   label: 'Email' },
                { value: 'memo',    label: 'Memo' },
                { value: 'internal',label: 'Internal' },
              ]} />
          </Field>
        </div>
        <div style={{ flex: 1 }}>
          <Field label="Subtype" hint="DealCloud note type — the call subtypes drive the call KPIs.">
            <Combobox value={draft.dc_note_subtype} searchable
              onChange={(e) => setDraft({
                ...draft, dc_note_subtype: e.target.value,
                // KPI call subtypes get a call date too — default today.
                call_date: (window.KPI_CALL_SUBTYPES || []).includes(e.target.value)
                  ? (draft.call_date || window.localISODate()) : draft.call_date,
              })}
              options={[{ value: '', label: '— none —' }, ...window.NOTE_SUBTYPES]} />
          </Field>
        </div>
      </div>
      {showCallDate && (
        <Field label="Call date" hint="When the call happened — the call KPIs bucket on this, not on when the note was logged.">
          <TextInput type="date" value={draft.call_date}
            onChange={(e) => setDraft({ ...draft, call_date: e.target.value })} style={{ width: P ? '100%' : 160 }} />
        </Field>
      )}
      {/* First-call capture: while the picked company has no lead source,
          ask here — the pick saves with the note (fill-only). */}
      {companyId && window.LeadSourceNudge && (
        <div style={{ marginBottom: 10 }}>
          <window.LeadSourceNudge companyId={companyId} value={draft.lead}
            onChange={(lead) => setDraft({ ...draft, lead })} />
        </div>
      )}
      <Field label="Also tag" hint="Cross-file the note on more companies or contacts — it shows on their pages too (like DealCloud's Companies/Contacts note fields).">
        <window.NoteTagPicker
          companies={draft.tag_companies}
          contacts={draft.tag_contacts}
          onCompanies={(v) => setDraft({ ...draft, tag_companies: v })}
          onContacts={(v) => setDraft({ ...draft, tag_contacts: v })} />
      </Field>
      <Field label="Note">
        <window.NoteBodyEditor
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          onAttachFiles={activeUser ? addPendingFiles : null}
          placeholder="Capture verbatim if useful…"
          rows={8}
          minHeight={140}
        />
      </Field>
      <Field label="Attachments" hint="Upload when the note is saved.">
        <window.ComposerAttachments files={pendingFiles} onChange={setPendingFiles} disabled={!activeUser} />
      </Field>
      {/* Portaled to body so the stacked drawer isn't nested inside this
          drawer's <Field> label (label click-forwarding + focus traps). */}
      {creatingCompany && ReactDOM.createPortal(
        <window.NewCompanyDrawer
          industries={companyLookups?.industries || []}
          members={companyLookups?.members || []}
          onClose={() => setCreatingCompany(false)}
          onCreated={(company) => {
            setCreatingCompany(false);
            if (company?.id) {
              setCompanies((prev) => [{ value: company.id, label: company.name }, ...prev]);
              setCompanyId(company.id);
            }
            window.dispatchEvent(new CustomEvent('atlas-data-changed'));
          }}
        />, document.body)}
    </Drawer>
  );
};

// ── Phone ───────────────────────────────────────────────────────────────────
// The Notes page on a phone: a search field, the type filters as chips, and
// a card feed — who, when, what it's about, three lines of the note. A tap
// opens the full-screen note reader on the same #/notes/<id> route. No table
// mode, export or column menu here, and nothing writes the desktop layout
// prefs (useColumnPrefs is never touched).
const PHONE_NOTES_PAGE = 40;

const PHONE_NOTE_ICON = { call: 'Phone', meeting: 'Calendar', email: 'Mail', ai_summary: 'Sparkle' };

// Card preview text: markdown structure and the toolbar's inline markers off,
// whitespace collapsed (the card clamps it to three lines).
const _phoneNotePlain = (s) => (s || '')
  .replace(/```[\s\S]*?```/g, ' ')
  .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/^[ \t]*#{1,6}[ \t]+/gm, '')
  .replace(/^[ \t]*>[ \t]?/gm, '')
  .replace(/^[ \t]*(?:[-*]|\d+[.)])[ \t]+/gm, '• ')
  .replace(/\*\*|~~|`/g, '')
  .replace(/(^|\s)\*(\S[^*\n]*?)\*(?=\s|$|[.,;:!?])/g, '$1$2')
  .replace(/\s+/g, ' ')
  .trim();

const PhoneNoteCard = ({ n, ai, onOpen }) => {
  const hue = NOTE_TYPE_HUE[n.note_type] || 'slate';
  const h = (window.HUE && (window.HUE[hue] || window.HUE.slate)) || { bg: 'var(--surface-2)', fg: 'var(--text-3)' };
  const I = Icon[PHONE_NOTE_ICON[n.note_type]] || Icon.Doc;
  const when = window.NoteCardDate(n);
  const thisYear = when.getFullYear() === new Date().getFullYear();
  const date = when.toLocaleDateString([], thisYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
  const preview = _phoneNotePlain(ai || n.body);
  const files = (n.attachments || []).length;
  const tagged = (n.tagged_companies || []).length + (n.tagged_contacts || []).length;
  // What the note is about — the record names themselves, not "Company: X".
  const records = [
    n.engagement_name && { key: 'e', icon: Icon.Pipeline, label: n.engagement_name },
    n.company_name && { key: 'c', icon: Icon.Building, label: n.company_name },
    n.contact_name && { key: 'p', icon: Icon.User, label: n.contact_name },
  ].filter(Boolean);
  const subtype = n.dc_note_subtype && n.dc_note_subtype !== 'Other' ? n.dc_note_subtype : null;
  return (
    <button type="button" onClick={onOpen}
      style={{
        display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', border: 'none',
        borderBottom: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)',
        fontFamily: 'inherit', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 26, height: 26, borderRadius: 999, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: h.bg, color: h.fg }}>
          <I size={13} />
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {n.author_name || (n.external_source ? (window.IntegrationSourceLabel ? window.IntegrationSourceLabel(n.external_source) : n.external_source) : 'Note')}
        </span>
        {n.is_pinned && <span aria-label="Pinned" style={{ display: 'inline-flex', color: 'var(--amber-fg)' }}><Icon.Pin size={13} /></span>}
        {files > 0 && <span style={{ fontSize: 13, color: 'var(--text-3)', flexShrink: 0 }}>📎 {files}</span>}
        <span style={{ fontSize: 13, color: 'var(--text-3)', flexShrink: 0 }}>{date}</span>
      </div>
      {n.title && (
        <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text)', marginTop: 6, lineHeight: 1.3,
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', wordBreak: 'break-word' }}>
          {n.title}
        </div>
      )}
      {preview && (
        <div style={{ fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.45, marginTop: n.title ? 3 : 6,
          display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden', wordBreak: 'break-word' }}>
          {ai && (
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--accent-text)', border: '1px solid var(--border)',
              borderRadius: 4, padding: '0 4px', marginRight: 6, verticalAlign: '1px' }}>AI</span>
          )}
          {preview}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px', marginTop: 8, fontSize: 13, color: 'var(--text-3)' }}>
        <Pill hue={hue} sz="sm">{window.NoteTypeLabel ? window.NoteTypeLabel(n) : n.note_type}</Pill>
        {subtype && <Pill hue="violet" sz="sm">{subtype}</Pill>}
        {records.map((r) => {
          const RI = r.icon;
          return (
            <span key={r.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0, maxWidth: '100%' }}>
              <span style={{ display: 'inline-flex', flexShrink: 0 }}><RI size={12} /></span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.label}</span>
            </span>
          );
        })}
        {tagged > 0 && <span>+{tagged} tagged</span>}
      </div>
    </button>
  );
};

const PhoneNotesView = ({ activeUser, openNoteId, setOpenNoteId }) => {
  const [tab, setTab] = React.useState('all');
  const [q, setQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [items, setItems] = React.useState(null);   // null = first page loading
  const [total, setTotal] = React.useState(0);
  const [error, setError] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(false);
  const [loadedAll, setLoadedAll] = React.useState(false);
  const [composing, setComposing] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const reqSeq = React.useRef(0);
  const aiPreviews = (window.useNoteAiPreviews || (() => ({})))(items);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Same paging + stale-response guard as the desktop feed, smaller pages
  // (a phone shows ~5 cards a screen).
  const fetchPage = React.useCallback(async (p, append) => {
    const seq = ++reqSeq.current;
    const params = {};
    if (tab === 'pinned') params.pinned_only = true;
    else if (tab !== 'all') params.note_type = tab;
    if (debouncedQ) params.q = debouncedQ;
    setPaging(true);
    if (!append) setError('');
    try {
      const res = await window.AtlasApi.listNotes({ ...params, page: p, limit: PHONE_NOTES_PAGE });
      if (seq !== reqSeq.current) return;
      const rows = (res && res.items) || [];
      const newTotal = res?.total ?? rows.length;
      setItems((prev) => {
        const next = append && prev ? [...prev, ...rows] : rows;
        if (rows.length === 0 || next.length >= newTotal) setLoadedAll(true);
        return next;
      });
      setTotal(newTotal);
      setPage(p);
    } catch (e) {
      if (seq !== reqSeq.current) return;
      if (!append) { setItems([]); setTotal(0); }
      setLoadedAll(true);
      setError((e && e.message) || 'Could not load notes');
    } finally {
      if (seq === reqSeq.current) setPaging(false);
    }
  }, [tab, debouncedQ]);

  const reload = React.useCallback(() => {
    setItems(null); setPage(1); setLoadedAll(false);
    return fetchPage(1, false);
  }, [fetchPage]);
  React.useEffect(() => { reload(); }, [reload]);

  const hasMore = items != null && !loadedAll && items.length < total;
  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !paging) fetchPage(page + 1, true);
    }, { rootMargin: '400px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, paging, page, fetchPage]);

  const filtered = !!debouncedQ || tab !== 'all';
  const tabLabel = (NOTE_TYPES.find((t) => t.key === tab) || {}).label;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
      <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
        <div style={{ padding: '0 16px' }}>
          <window.PhoneSearchField value={q} onChange={setQ} placeholder="Search notes" />
        </div>
        <window.PhoneChips value={tab} onChange={setTab} style={{ marginTop: 10 }}
          options={NOTE_TYPES.map(({ key, label }) => ({ value: key, label }))} />
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        {/* Bottom padding clears the New note button (and the home indicator
            when there is no tab bar under the page). */}
        <window.PhoneScroll style={{ paddingBottom: 'calc(96px + max(0px, var(--sab, 0px) - var(--tabbar-h, 0px)))' }}>
          {items === null && <SkeletonRows rows={8} variant="generic" />}

          {items !== null && items.length > 0 && (
            <div style={{ padding: '10px 16px 6px', fontSize: 13, color: 'var(--text-3)' }}>
              {total.toLocaleString()} {total === 1 ? 'note' : 'notes'}
              {tab !== 'all' && tabLabel ? ` · ${tabLabel}` : ''}
              {debouncedQ ? ` matching “${debouncedQ}”` : ''}
            </div>
          )}

          {items !== null && items.length === 0 && (
            error ? (
              <window.PhoneEmpty title="Couldn't load notes" body={error}
                action={<Btn size="md" variant="outline" onClick={reload}>Try again</Btn>} />
            ) : (
              <window.PhoneEmpty
                title={filtered ? 'No notes match' : 'No notes yet'}
                body={filtered ? 'Try another word or filter.' : null}
                action={filtered ? <Btn size="md" variant="outline" onClick={() => { setQ(''); setTab('all'); }}>Clear filters</Btn> : null} />
            )
          )}

          {items !== null && items.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {items.map((n) => (
                <PhoneNoteCard key={n.id} n={n} ai={(aiPreviews && aiPreviews[n.id]) || n.ai_preview || null}
                  onOpen={() => setOpenNoteId(n.id)} />
              ))}
            </div>
          )}

          {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
          {paging && items !== null && items.length > 0 && <SkeletonRows rows={2} variant="generic" />}
          {items !== null && items.length > 0 && error && (
            <div style={{ padding: '14px 16px', textAlign: 'center', fontSize: 14, color: 'var(--rose-fg)' }}>
              Couldn't load more. <button type="button" onClick={() => { setError(''); setLoadedAll(false); fetchPage(page + 1, true); }}
                style={{ border: 'none', background: 'transparent', color: 'var(--accent-text)', fontSize: 14, fontWeight: 600, padding: '8px 4px', cursor: 'pointer' }}>Retry</button>
            </div>
          )}
          {!hasMore && !error && items !== null && items.length > 0 && (
            <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: 'var(--text-4)' }}>
              That's all {items.length.toLocaleString()} {items.length === 1 ? 'note' : 'notes'}
            </div>
          )}
        </window.PhoneScroll>
      </div>

      {activeUser && (
        <button type="button" aria-label="New note" onClick={() => setComposing(true)}
          style={{
            position: 'absolute', right: 16, bottom: 'max(16px, calc(var(--sab, 0px) + 16px - var(--tabbar-h, 0px)))', width: 54, height: 54,
            borderRadius: 999, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-lg)',
            WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
          }}>
          <Icon.Plus size={22} />
        </button>
      )}

      <NoteModal noteId={openNoteId} onClose={() => setOpenNoteId(null)} onChange={reload} />
      {composing && (
        <NewNoteDrawer
          activeUser={activeUser}
          onClose={() => setComposing(false)}
          onCreated={() => { setComposing(false); reload(); }}
        />
      )}
    </div>
  );
};

window.NotesView = NotesView;
